import { CellWithTooltip } from "@/components/data-table/CellWithTooltip";
import { ColumnHeader } from "@/components/data-table/ColumnHeader";
import { Badge } from "@/components/ui/badge";
import { AppSubscriptionStatus } from "@/gql/__generated__/graphql";
import { cn } from "@/lib/utils";
import { ColumnDef } from "@tanstack/react-table";
import { format } from "date-fns";
import { SubscriptionActions } from "../components/Subscriptions/SubscriptionActions";
import { SubscriptionTable } from "../types/subscriptions.type";
import { sortByPrice } from "../utils/sortByPrice";

export type SubscriptionColumn = ColumnDef<SubscriptionTable>;

const appImageColumn: SubscriptionColumn = {
  accessorKey: "appImage",
  cell: ({ row }) => (
    <div className="h-full w-fit">
      <img
        className="object-cover h-12 rounded"
        src={row.getValue("appImage") || "/app-placeholder-picture.png"}
        alt={row.getValue("appImage") || ""}
      />
    </div>
  ),
  header: undefined,
  enableHiding: false
};

const nameColumn: SubscriptionColumn = {
  accessorKey: "appName",
  cell: ({ row }) => (
    <CellWithTooltip
      data-testid="appName"
      cellValue={row.getValue("appName")}
    />
  ),
  header: ({ column }) => <ColumnHeader column={column} title="App" />,
  enableHiding: false
};

const organizationNameColumn: SubscriptionColumn = {
  accessorKey: "organizationName",
  cell: ({ row }) => (
    <CellWithTooltip cellValue={row.getValue("organizationName")} />
  ),
  header: ({ column }) => <ColumnHeader column={column} title="Organization" />,
  enableHiding: false
};

const statusColumn: SubscriptionColumn = {
  accessorKey: "status",
  cell: ({ row }) => {
    const status: AppSubscriptionStatus = row.getValue("status");
    return (
      <Badge
        variant="secondary"
        className="font-normal"
        data-testid="status-cell"
      >
        <div
          className={cn(
            "w-2 h-2 rounded-full mr-2",
            {
              [AppSubscriptionStatus.Active]: "bg-brand-primary",
              [AppSubscriptionStatus.Withdrawn]: "bg-brand-error",
              [AppSubscriptionStatus.Cancelled]: "bg-brand-error",
              [AppSubscriptionStatus.Expired]: "bg-yellow-400"
            }[status]
          )}
        />
        <div className="lowercase first-letter:capitalize dark:text-brand-gray-200">
          {status}
        </div>
      </Badge>
    );
  },
  header: ({ column }) => <ColumnHeader column={column} title="Status" />,
  enableHiding: false
};

const referenceColumn: SubscriptionColumn = {
  accessorKey: "reference",
  cell: ({ row }) => (
    <CellWithTooltip cellValue={row.getValue("reference") || ""} />
  ),
  header: ({ column }) => <ColumnHeader column={column} title="Reference" />,
  enableHiding: false
};

const createdAtColumn: SubscriptionColumn = {
  accessorKey: "createdAt",
  cell: ({ row }) => <span>{format(row.getValue("createdAt"), "PP")}</span>,
  header: ({ column }) => <ColumnHeader column={column} title="Accepted at" />,
  enableHiding: false
};

const renewsAtColumn: SubscriptionColumn = {
  accessorKey: "renewsAt",
  cell: ({ row }) => {
    const status: AppSubscriptionStatus = row.getValue("status");
    const renewsAt: SubscriptionTable["renewsAt"] = row.getValue("renewsAt");

    if (!renewsAt || status !== AppSubscriptionStatus.Active) return null;

    return <span data-testid="renewsAt">{format(renewsAt, "PP")}</span>;
  },
  header: ({ column }) => <ColumnHeader column={column} title="Renews at" />,
  enableHiding: false
};

const expiresAtColumn: SubscriptionColumn = {
  accessorKey: "expiresAt",
  cell: ({ row }) => {
    const status: AppSubscriptionStatus = row.getValue("status");
    const expiresAt: SubscriptionTable["expiresAt"] = row.getValue("expiresAt");

    if (!expiresAt || status === AppSubscriptionStatus.Active) return null;

    return <span data-testid="expiresAt">{format(expiresAt, "PP")}</span>;
  },
  header: ({ column }) => <ColumnHeader column={column} title="Expires at" />,
  enableHiding: false
};

const monthlyPriceUSDColumn: SubscriptionColumn = {
  accessorKey: "monthlyPriceUSD",
  cell: ({ row }) => {
    const price: SubscriptionTable["monthlyPriceUSD"] =
      row.getValue("monthlyPriceUSD");
    if (!price) return <span data-testid="priceCell">Free</span>;
    return <span data-testid="priceCell">${price}</span>;
  },
  header: ({ column }) => <ColumnHeader column={column} title="Price" />,
  sortingFn: sortByPrice,
  enableHiding: false
};

const actionColumn: SubscriptionColumn = {
  id: "action",
  cell: ({ row }) => <SubscriptionActions subscription={row.original} />
};

export const outboundSubscriptionColumns: SubscriptionColumn[] = [
  appImageColumn,
  nameColumn,
  organizationNameColumn,
  statusColumn,
  referenceColumn,
  createdAtColumn,
  renewsAtColumn,
  expiresAtColumn,
  monthlyPriceUSDColumn,
  actionColumn
];

export const inboundSubscriptionColumns: SubscriptionColumn[] = [
  appImageColumn,
  nameColumn,
  organizationNameColumn,
  statusColumn,
  createdAtColumn,
  renewsAtColumn,
  expiresAtColumn,
  monthlyPriceUSDColumn,
  actionColumn
];
